import type React from "react"
import { useState } from "react"
import { format, subMonths } from "date-fns"
import { CalendarIcon, Check, ChevronDown } from "lucide-react"
import type { DateRange } from "react-day-picker"

interface FilterSectionProps {
  team: string
  setTeam: (value: string) => void
  salesRep: string
  setSalesRep: (value: string) => void
  dateRange: DateRange | undefined
  setDateRange: React.Dispatch<React.SetStateAction<DateRange | undefined>>
  forecastFilter: string
  setForecastFilter: (value: string) => void
}

// Custom Components
const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`bg-white text-black rounded-lg border border-gray-200 shadow-sm ${className}`}>{children}</div>
)

const Button = ({
  children,
  variant = "default",
  className = "",
  onClick,
}: {
  children: React.ReactNode
  variant?: "default" | "outline"
  className?: string
  onClick?: () => void
}) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background h-10 py-2 px-4"
  const variantClasses =
    variant === "outline"
      ? "border border-gray-300 bg-white hover:bg-gray-50 hover:text-gray-900"
      : "bg-blue-600 text-white hover:bg-blue-700"

  return (
    <button className={`${baseClasses} ${variantClasses} ${className}`} onClick={onClick}>
      {children}
    </button>
  )
}

// Custom Dropdown Component
const Dropdown = ({
  title,
  options,
  value,
  onChange,
  className = "",
}: {
  title: string
  options: { label: string; value: string }[]
  value: string
  onChange: (value: string) => void
  className?: string
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const selectedOption = options.find((option) => option.value === value)

  return (
    <div className={`relative ${className}`}>
      <Button variant="outline" className="justify-between w-full md:w-[200px]" onClick={() => setIsOpen(!isOpen)}>
        {value ? selectedOption?.label : title}
        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {isOpen && (
        <div className="absolute z-50 w-full md:w-[200px] mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="py-1">
            {options.map((option) => (
              <button
                key={option.value}
                className="flex items-center w-full px-3 py-2 text-sm hover:bg-gray-100 text-left"
                onClick={() => {
                  onChange(option.value)
                  setIsOpen(false)
                }}
              >
                <Check className={`mr-2 h-4 w-4 ${value === option.value ? "opacity-100" : "opacity-0"}`} />
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Simple Date Range Picker
const DateRangePicker = ({
  dateRange,
  onDateRangeChange,
  className = "",
}: {
  dateRange: DateRange | undefined
  onDateRangeChange: (range: DateRange | undefined) => void
  className?: string
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const formatDateRange = () => {
    if (dateRange?.from && dateRange?.to) {
      return `${format(dateRange.from, "MMM dd, yyyy")} - ${format(dateRange.to, "MMM dd, yyyy")}`
    }
    if (dateRange?.from) {
      return format(dateRange.from, "MMM dd, yyyy")
    }
    return "Pick a date range"
  }

  const presetRanges = [
    {
      label: "Last 30 days",
      range: {
        from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        to: new Date(),
      },
    },
    {
      label: "Last 3 months",
      range: {
        from: subMonths(new Date(), 3),
        to: new Date(),
      },
    },
    {
      label: "Last 6 months",
      range: {
        from: subMonths(new Date(), 6),
        to: new Date(),
      },
    },
    {
      label: "This year",
      range: {
        from: new Date(new Date().getFullYear(), 0, 1),
        to: new Date(),
      },
    },
  ]

  return (
    <div className={`relative ${className}`}>
      <Button
        variant="outline"
        className="justify-start text-left font-normal w-full md:w-[300px]"
        onClick={() => setIsOpen(!isOpen)}
      >
        <CalendarIcon className="mr-2 h-4 w-4" />
        {formatDateRange()}
      </Button>

      {isOpen && (
        <div className="absolute z-50 w-full md:w-[300px] mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="p-3">
            <div className="space-y-2">
              {presetRanges.map((preset) => (
                <button
                  key={preset.label}
                  className="flex items-center w-full px-3 py-2 text-sm hover:bg-gray-100 rounded text-left"
                  onClick={() => {
                    onDateRangeChange(preset.range)
                    setIsOpen(false)
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export function FilterSection({
  team,
  setTeam,
  salesRep,
  setSalesRep,
  dateRange,
  setDateRange,
  forecastFilter,
  setForecastFilter,
}: FilterSectionProps) {
  // Filter options
  const teamOptions = [
    { label: "All Teams", value: "all" },
    { label: "Enterprise", value: "enterprise" },
    { label: "Mid-Market", value: "midmarket" },
    { label: "SMB", value: "smb" },
  ]

  const repOptions = [
    { label: "All Reps", value: "all" },
    { label: "Jack Doheny", value: "Jack Doheny" },
    { label: "Sarah Johnson", value: "Sarah Johnson" },
    { label: "Michael Brown", value: "Michael Brown" },
  ]

  const forecastFilterOptions = [
    { label: "Full Forecast", value: "full" },
    { label: "Committed", value: "committed" },
    { label: "Best Case", value: "best" },
    { label: "Worst Case", value: "worst" },
  ]

  return (
    <Card className="p-4">
      <div className="flex flex-col md:flex-row gap-4">
        <Dropdown title="Team" options={teamOptions} value={team} onChange={setTeam} />
        <Dropdown title="Sales Rep" options={repOptions} value={salesRep} onChange={setSalesRep} />
        <Dropdown
          title="Forecast Type"
          options={forecastFilterOptions}
          value={forecastFilter}
          onChange={setForecastFilter}
        />
        <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
      </div>
    </Card>
  )
}